import api from './api'

/**
 * Drive Docs Service
 * API calls for Google Drive integration - Project documents
 */

// Helper to check if user is authenticated
const isAuthenticated = () => {
  return !!localStorage.getItem('access_token')
}

const driveService = {
  /**
   * Lấy danh sách dự án (root level folders)
   * @param {string} sortMode - Sort mode: 'manual' | 'number' | 'name'
   * @returns {Promise<{status: string, data: Array}>}
   */
  async getProjects(sortMode = 'manual') {
    try {
      const endpoint = isAuthenticated() ? '/drive/projects' : '/drive/public/projects'
      const response = await api.get(endpoint, {
        params: { sort: sortMode }
      })
      return response.data
    } catch {
      return { status: 'success', data: [] }
    }
  },

  /**
   * Lấy chi tiết dự án với files grouped by category
   * @param {string} driveId - Google Drive folder ID
   * @returns {Promise<{status: string, data: Object}>}
   */
  async getProjectDetail(driveId) {
    try {
      const endpoint = isAuthenticated()
        ? `/drive/projects/${driveId}`
        : `/drive/public/projects/${driveId}`
      const response = await api.get(endpoint)
      return response.data
    } catch {
      return {
        status: 'error',
        data: null,
        message: 'Không thể tải thông tin dự án'
      }
    }
  },

  /**
   * Lấy thêm folders trong dự án (cho "Xem thêm")
   * @param {string} driveId - Project drive ID
   * @returns {Promise<{status: string, data: Array}>}
   */
  async getProjectMore(driveId) {
    try {
      const endpoint = isAuthenticated()
        ? `/drive/projects/${driveId}/more`
        : `/drive/public/projects/${driveId}/more`
      const response = await api.get(endpoint)
      return response.data
    } catch {
      return { status: 'success', data: [] }
    }
  },

  /**
   * Lấy nội dung folder - files và subfolders
   * @param {string} driveId - Folder drive ID
   * @returns {Promise<{status: string, data: Object}>}
   */
  async getFolderContents(driveId) {
    try {
      const endpoint = isAuthenticated()
        ? `/drive/folders/${driveId}`
        : `/drive/public/folders/${driveId}`
      const response = await api.get(endpoint)
      return response.data
    } catch {
      return {
        status: 'error',
        data: null,
        message: 'Không thể tải nội dung folder'
      }
    }
  },

  /**
   * Tìm kiếm files
   * @param {string} query - Search query
   * @param {number} limit - Max results
   * @returns {Promise<{status: string, data: Array}>}
   */
  async search(query, limit = 50) {
    try {
      const endpoint = isAuthenticated() ? '/drive/search' : '/drive/public/search'
      const response = await api.get(endpoint, {
        params: { q: query, limit }
      })
      return response.data
    } catch {
      return { status: 'success', data: [] }
    }
  },

  /**
   * Track file access (khi user click vào file)
   * Only works for authenticated users
   * @param {string} driveId - File drive ID
   */
  async trackAccess(driveId) {
    // Only track if authenticated
    if (!isAuthenticated()) return

    try {
      await api.post(`/drive/access/${driveId}`)
    } catch {
      // Silent fail - không quan trọng
    }
  },

  /**
   * Lấy trạng thái sync
   * @returns {Promise<{status: string, data: Object}>}
   */
  async getSyncStatus() {
    try {
      const endpoint = isAuthenticated() ? '/drive/status' : '/drive/public/status'
      const response = await api.get(endpoint)
      return response.data
    } catch {
      return {
        status: 'success',
        data: { sync_status: 'unknown' }
      }
    }
  },

  // ============================================
  // Admin APIs
  // ============================================

  /**
   * Lấy config (admin)
   * @returns {Promise<{status: string, data: Object}>}
   */
  async getConfig() {
    try {
      const response = await api.get('/drive/admin/config')
      return response.data
    } catch {
      return {
        status: 'error',
        data: null,
        message: 'Không thể tải cấu hình'
      }
    }
  },

  /**
   * Cập nhật config (admin)
   * @param {string} rootFolderId - Google Drive folder ID
   * @returns {Promise<{status: string, data: Object, message: string}>}
   */
  async updateConfig(rootFolderId) {
    try {
      const response = await api.put('/drive/admin/config', {
        root_folder_id: rootFolderId
      })
      return response.data
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message || 'Cập nhật thất bại'
      }
    }
  },

  /**
   * Trigger sync from Google Drive (admin - nút Reload)
   * @returns {Promise<{status: string, data: Object, message: string}>}
   */
  async syncDrive() {
    try {
      const response = await api.post('/drive/admin/sync')
      return response.data
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message || 'Đồng bộ thất bại'
      }
    }
  },

  /**
   * Force reset sync status (admin - khi sync bị kẹt)
   * @returns {Promise<{status: string, data: Object, message: string}>}
   */
  async resetSyncStatus() {
    try {
      const response = await api.post('/drive/admin/sync/reset')
      return response.data
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message || 'Reset thất bại'
      }
    }
  },

  /**
   * Reorder projects (admin - kéo thả thủ công)
   * @param {Array<{drive_id: string, display_order: number}>} items
   * @returns {Promise<{status: string, message: string}>}
   */
  async reorderProjects(items) {
    try {
      const response = await api.put('/drive/admin/reorder', { items })
      return response.data
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message || 'Sắp xếp thất bại'
      }
    }
  },

  /**
   * Get pause status (admin)
   * @returns {Promise<{status: string, data: Object}>}
   */
  async getPauseStatus() {
    try {
      const response = await api.get('/drive/admin/sync/pause-status')
      return response.data
    } catch {
      return {
        status: 'success',
        data: { is_paused: false, can_resume: false }
      }
    }
  },

  /**
   * Resume paused sync (admin)
   * @returns {Promise<{status: string, message: string}>}
   */
  async resumeSync() {
    try {
      const response = await api.post('/drive/admin/sync/resume')
      return response.data
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message || 'Không thể tiếp tục đồng bộ'
      }
    }
  },

  /**
   * Get folder tree for Quick Sync dropdown (admin)
   * @param {number} maxDepth - Max depth to fetch (default 2)
   * @returns {Promise<{status: string, data: Array}>}
   */
  async getFolderTree(maxDepth = 2) {
    try {
      const response = await api.get('/drive/admin/folder-tree', {
        params: { max_depth: maxDepth }
      })
      return response.data
    } catch {
      return { status: 'success', data: [] }
    }
  },

  /**
   * Get SSE URL for quick sync (single folder)
   * @param {string} folderId - Drive folder ID to sync
   * @param {string} token - Access token
   * @returns {string} SSE URL
   */
  getQuickSyncStreamUrl(folderId, token) {
    const baseUrl = api.defaults.baseURL || ''
    return `${baseUrl}/drive/admin/sync/folder/${folderId}/stream?token=${token}`
  },

  /**
   * Get all projects with descriptions (admin)
   * @returns {Promise<{status: string, data: Array}>}
   */
  async getProjectDescriptions() {
    try {
      const response = await api.get('/drive/admin/projects/descriptions')
      return response.data
    } catch {
      return { status: 'success', data: [] }
    }
  },

  /**
   * Update project description (admin)
   * @param {string} driveId - Project drive ID
   * @param {string} description - New description text
   * @returns {Promise<{status: string, data: Object, message: string}>}
   */
  async updateProjectDescription(driveId, description) {
    try {
      const response = await api.put(`/drive/admin/projects/${driveId}/description`, {
        description
      })
      return response.data
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message || 'Cập nhật mô tả thất bại'
      }
    }
  },

  // ============================================
  // Helper functions
  // ============================================

  /**
   * Copy link to clipboard và track access
   * @param {Object} file - File object với view_url
   * @returns {Promise<boolean>} - Success status
   */
  async copyLink(file) {
    if (!file?.view_url) return false

    try {
      await navigator.clipboard.writeText(file.view_url)
      // Track access khi copy
      if (file.drive_id) {
        this.trackAccess(file.drive_id)
      }
      return true
    } catch {
      return false
    }
  },

  /**
   * Get category label in Vietnamese
   * @param {string} category - Category key
   * @returns {string} Vietnamese label
   */
  getCategoryLabel(category) {
    const labels = {
      bang_gia: 'Bảng giá',
      csbh: 'Chính sách BH',
      mat_bang: 'Mặt bằng',
      phap_ly: 'Pháp lý',
      media: 'Media',
      tien_ich: 'Tiện ích',
      khac: 'Khác'
    }
    return labels[category] || category
  },

  /**
   * Get category icon
   * @param {string} category - Category key
   * @returns {string} Emoji icon
   */
  getCategoryIcon(category) {
    const icons = {
      bang_gia: '💰',
      csbh: '📋',
      mat_bang: '🏠',
      phap_ly: '📜',
      media: '🎬',
      tien_ich: '✨',
      khac: '📁'
    }
    return icons[category] || '📄'
  },

  /**
   * Format file size
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted size
   */
  formatFileSize(bytes) {
    if (!bytes) return ''
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  },

  /**
   * Get thumbnail URL for a Drive file
   * Uses backend proxy to fetch thumbnails via Service Account credentials
   * This allows thumbnails for files that are not publicly shared
   * @param {string} driveId - Google Drive file ID
   * @param {number} size - Thumbnail width in pixels (default 400)
   * @returns {string|null} Thumbnail URL
   */
  getThumbnailUrl(driveId, size = 400) {
    if (!driveId) return null
    // Use backend proxy instead of direct Google URL
    const baseUrl = api.defaults.baseURL || ''
    return `${baseUrl}/drive/thumbnail/${driveId}?size=${size}`
  },

  /**
   * Get direct Google Drive thumbnail URL (may not work for non-public files)
   * @deprecated Use getThumbnailUrl() which uses backend proxy
   * @param {string} driveId - Google Drive file ID
   * @param {number} size - Thumbnail width in pixels (default 400)
   * @returns {string|null} Thumbnail URL
   */
  getDirectThumbnailUrl(driveId, size = 400) {
    if (!driveId) return null
    return `https://drive.google.com/thumbnail?id=${driveId}&sz=w${size}`
  },

  /**
   * Check if a mime type supports thumbnail generation
   * Google Drive can generate thumbnails for these file types
   * @param {string} mimeType - File MIME type
   * @returns {boolean}
   */
  supportsThumbnail(mimeType) {
    const mime = (mimeType || '').toLowerCase()
    return (
      mime.includes('image') ||
      mime.includes('pdf') ||
      mime.includes('document') ||
      mime.includes('word') ||
      mime.includes('spreadsheet') ||
      mime.includes('excel') ||
      mime.includes('presentation') ||
      mime.includes('powerpoint') ||
      mime.includes('video')
    )
  },

  /**
   * Get preview URL for embedding in iframe
   * @param {Object} file - File object with drive_id and mime_type
   * @returns {string|null} Preview URL suitable for iframe
   */
  getPreviewUrl(file) {
    if (!file?.drive_id) return null

    const mime = (file.mime_type || '').toLowerCase()

    // Google Docs/Sheets/Slides - use embedded view
    if (mime.includes('google-apps')) {
      return file.view_url?.replace('/edit', '/preview') || null
    }

    // PDF - use Google Drive PDF viewer
    if (mime.includes('pdf')) {
      return `https://drive.google.com/file/d/${file.drive_id}/preview`
    }

    // Images - direct view
    if (mime.includes('image')) {
      return `https://drive.google.com/uc?export=view&id=${file.drive_id}`
    }

    // Video - use Google Drive video player
    if (mime.includes('video')) {
      return `https://drive.google.com/file/d/${file.drive_id}/preview`
    }

    // Other files - try preview mode
    return `https://drive.google.com/file/d/${file.drive_id}/preview`
  }
}

export default driveService
